import type { POSOrder, TenderType } from '@/lib/types/pos'
import type { POSPrinterMethod, POSReceiptWidth } from '@/lib/store/usePOSPrinterStore'
import { STRAPI_BASE_URL } from '@/config/api'
import { DEFAULT_POS_CURRENCY, formatPOSMoney } from './currency'

export interface PrinterCapabilities {
  systemPrint: boolean
  webUsb: boolean
  webSerial: boolean
  webBluetooth: boolean
}

export interface ReceiptRestaurant {
  name?: string
  email?: string
  phone?: string
  currency?: string
  address?: {
    address1?: string
    address2?: string
    city?: string
    country?: string
  }
  ticketLogo?: {
    url?: string
  } | null
}

export interface ReceiptPaymentSummary {
  amountPaid: number
  tipAmount: number
  taxTotal: number
  change: number
  tenderType: TenderType | string
}

export interface ReceiptPrintInput {
  order: POSOrder
  restaurant?: ReceiptRestaurant | null
  payment?: ReceiptPaymentSummary | null
  width: POSReceiptWidth
}

export interface PrintReceiptOptions extends ReceiptPrintInput {
  method: POSPrinterMethod
}

const encoder = new TextEncoder()

function money(n: number, currency = DEFAULT_POS_CURRENCY) {
  return formatPOSMoney(n, currency)
}

function itemQuantityLabel(item: { quantity: number; weightGrams?: number | null }) {
  if (item.weightGrams != null) {
    const grams = Number(item.weightGrams)
    return grams % 1000 === 0 ? `${grams / 1000} kg` : `${grams} g`
  }
  return `${item.quantity} x`
}

function escapeHtml(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function logoUrl(restaurant?: ReceiptRestaurant | null) {
  const url = restaurant?.ticketLogo?.url
  if (!url) return null
  return url.startsWith('http') ? url : `${STRAPI_BASE_URL}${url}`
}

function receiptTotals(order: POSOrder, payment?: ReceiptPaymentSummary | null) {
  const items = (order.orderItems ?? []).filter((i) => i.status !== 'voided')
  const subtotal = items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
  const taxTotal = payment?.taxTotal ?? items.reduce((sum, item) => sum + item.unitPrice * item.quantity * (item.taxRate ?? 0), 0)
  const tipTotal = payment?.tipAmount ?? (order.payments ?? [])
    .filter((p) => p.status === 'completed')
    .reduce((sum, p) => sum + (p.tipAmount ?? 0), 0)
  const paidTotal = payment?.amountPaid ?? (order.payments ?? [])
    .filter((p) => p.status === 'completed')
    .reduce((sum, p) => sum + p.amount + (p.tipAmount ?? 0), 0)
  const total = subtotal + taxTotal + tipTotal
  return { items, subtotal, taxTotal, tipTotal, paidTotal, total }
}

export function detectPrinterCapabilities(): PrinterCapabilities {
  const nav = navigator as any
  return {
    systemPrint: typeof window !== 'undefined' && typeof window.print === 'function',
    webUsb: !!nav.usb,
    webSerial: !!nav.serial,
    webBluetooth: !!nav.bluetooth,
  }
}

export function buildReceiptHtml({ order, restaurant, payment, width }: ReceiptPrintInput) {
  const totals = receiptTotals(order, payment)
  const receiptMoney = (amount: number) => money(amount, restaurant?.currency)
  const printedAt = new Date().toLocaleString()
  const service = order.table?.name ? `Table ${order.table.name}` : order.serviceMode === 'takeaway' ? 'Takeaway' : 'Counter sale'
  const logo = logoUrl(restaurant)
  const address = [
    restaurant?.address?.address1,
    restaurant?.address?.address2,
    restaurant?.address?.city,
    restaurant?.address?.country,
  ].filter(Boolean).join(', ')

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Receipt ${escapeHtml(order.documentId)}</title>
  <style>
    @page { size: ${width} auto; margin: 4mm; }
    * { box-sizing: border-box; }
    body {
      width: ${width};
      margin: 0 auto;
      color: #111;
      background: #fff;
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
      font-size: 12px;
      line-height: 1.35;
    }
    .receipt { padding: 2mm; }
    .center { text-align: center; }
    .logo { max-width: ${width === '58mm' ? '42mm' : '58mm'}; max-height: 24mm; object-fit: contain; margin: 0 auto 6px; display: block; }
    .name { font-size: 16px; font-weight: 800; }
    .muted { color: #444; }
    .divider { border-top: 1px dashed #111; margin: 8px 0; }
    .row { display: flex; justify-content: space-between; gap: 8px; }
    .item-name { flex: 1; min-width: 0; overflow-wrap: anywhere; }
    .item-total { white-space: nowrap; }
    .total { font-size: 15px; font-weight: 800; }
    .small { font-size: 10px; }
    @media print {
      body { width: ${width}; }
    }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="center">
      ${logo ? `<img class="logo" src="${escapeHtml(logo)}" alt="" />` : ''}
      <div class="name">${escapeHtml(restaurant?.name ?? 'Restaurant')}</div>
      ${address ? `<div class="muted small">${escapeHtml(address)}</div>` : ''}
      ${restaurant?.phone ? `<div class="muted small">${escapeHtml(restaurant.phone)}</div>` : ''}
      ${restaurant?.email ? `<div class="muted small">${escapeHtml(restaurant.email)}</div>` : ''}
    </div>
    <div class="divider"></div>
    <div class="row small"><span>Receipt</span><span>${escapeHtml(order.documentId)}</span></div>
    <div class="row small"><span>Opened</span><span>${escapeHtml(new Date(order.openedAt).toLocaleString())}</span></div>
    <div class="row small"><span>Printed</span><span>${escapeHtml(printedAt)}</span></div>
    <div class="row small"><span>Service</span><span>${escapeHtml(service)}</span></div>
    <div class="divider"></div>
    ${totals.items.map((item) => `
      <div class="row">
        <span class="item-name">${escapeHtml(itemQuantityLabel(item))} ${escapeHtml(item.menuItem?.name ?? 'Item')}${item.variant ? ` (${escapeHtml(item.variant.name)})` : ''}</span>
        <span class="item-total">${receiptMoney(item.unitPrice * item.quantity)}</span>
      </div>
      ${item.notes ? `<div class="small muted">${escapeHtml(item.notes)}</div>` : ''}
    `).join('')}
    <div class="divider"></div>
    <div class="row"><span>Subtotal</span><span>${receiptMoney(totals.subtotal)}</span></div>
    <div class="row"><span>Tax</span><span>${receiptMoney(totals.taxTotal)}</span></div>
    ${totals.tipTotal > 0 ? `<div class="row"><span>Tip</span><span>${receiptMoney(totals.tipTotal)}</span></div>` : ''}
    <div class="row total"><span>Total</span><span>${receiptMoney(totals.total)}</span></div>
    <div class="divider"></div>
    <div class="row"><span>Paid</span><span>${receiptMoney(totals.paidTotal)}</span></div>
    ${payment?.change ? `<div class="row"><span>Change</span><span>${receiptMoney(payment.change)}</span></div>` : ''}
    ${payment?.tenderType ? `<div class="row"><span>Tender</span><span>${escapeHtml(String(payment.tenderType).replace('_', ' '))}</span></div>` : ''}
    <div class="divider"></div>
    <div class="center small">Thank you</div>
  </div>
</body>
</html>`
}

export function buildReceiptText(input: ReceiptPrintInput) {
  const { order, restaurant, payment } = input
  const totals = receiptTotals(order, payment)
  const receiptMoney = (amount: number) => money(amount, restaurant?.currency)
  const lineWidth = input.width === '58mm' ? 32 : 42
  const service = order.table?.name ? `Table ${order.table.name}` : order.serviceMode === 'takeaway' ? 'Takeaway' : 'Counter sale'
  const center = (s: string) => s.padStart(Math.max(0, Math.floor((lineWidth + s.length) / 2))).slice(0, lineWidth)
  const divider = '-'.repeat(lineWidth)
  const row = (left: string, right: string) => {
    const l = left.slice(0, Math.max(1, lineWidth - right.length - 1))
    return `${l}${' '.repeat(Math.max(1, lineWidth - l.length - right.length))}${right}`
  }

  const lines = [
    center(restaurant?.name ?? 'Restaurant'),
    ...(restaurant?.phone ? [restaurant.phone] : []),
    divider,
    row('Receipt', order.documentId.slice(-10)),
    row('Service', service),
    row('Printed', new Date().toLocaleString()),
    divider,
    ...totals.items.flatMap((item) => [
      row(`${itemQuantityLabel(item)} ${item.menuItem?.name ?? 'Item'}`, receiptMoney(item.unitPrice * item.quantity)),
      ...(item.variant ? [`  ${item.variant.name}`] : []),
      ...(item.notes ? [`  ${item.notes}`] : []),
    ]),
    divider,
    row('Subtotal', receiptMoney(totals.subtotal)),
    row('Tax', receiptMoney(totals.taxTotal)),
    ...(totals.tipTotal > 0 ? [row('Tip', receiptMoney(totals.tipTotal))] : []),
    row('Total', receiptMoney(totals.total)),
    divider,
    row('Paid', receiptMoney(totals.paidTotal)),
    ...(payment?.change ? [row('Change', receiptMoney(payment.change))] : []),
    ...(payment?.tenderType ? [row('Tender', String(payment.tenderType).replace('_', ' '))] : []),
    divider,
    center('Thank you'),
  ]

  return lines.join('\n')
}

function escposBytes(text: string) {
  const init = [0x1b, 0x40]
  const feedAndCut = [0x0a, 0x0a, 0x1d, 0x56, 0x00]
  return new Uint8Array([...init, ...Array.from(encoder.encode(text)), ...feedAndCut])
}

export async function printWithSystemDialog(input: ReceiptPrintInput) {
  const receiptWindow = window.open('', 'pos-receipt-print', 'width=420,height=720')
  if (!receiptWindow) throw new Error('Popup blocked. Allow popups to print receipts.')
  receiptWindow.document.write(buildReceiptHtml(input))
  receiptWindow.document.close()
  receiptWindow.focus()
  setTimeout(() => receiptWindow.print(), 250)
}

async function printWithWebUsb(input: ReceiptPrintInput) {
  const nav = navigator as any
  if (!nav.usb) throw new Error('WebUSB is not supported by this browser')
  const knownDevices = typeof nav.usb.getDevices === 'function' ? await nav.usb.getDevices() : []
  const device = knownDevices[0] ?? await nav.usb.requestDevice({ filters: [{ classCode: 7 }] })
  await device.open()
  if (!device.configuration) await device.selectConfiguration(1)
  const iface = device.configuration.interfaces.find((candidate: any) =>
    candidate.alternates.some((alt: any) => alt.endpoints.some((ep: any) => ep.direction === 'out')),
  )
  if (!iface) throw new Error('No writable USB printer interface found')
  await device.claimInterface(iface.interfaceNumber)
  const alternate = iface.alternates.find((alt: any) => alt.endpoints.some((ep: any) => ep.direction === 'out'))
  const endpoint = alternate.endpoints.find((ep: any) => ep.direction === 'out')
  await device.transferOut(endpoint.endpointNumber, escposBytes(buildReceiptText(input)))
  await device.releaseInterface(iface.interfaceNumber).catch(() => undefined)
  await device.close().catch(() => undefined)
  return device.productName || device.manufacturerName || 'USB printer'
}

async function printWithWebSerial(input: ReceiptPrintInput) {
  const nav = navigator as any
  if (!nav.serial) throw new Error('WebSerial is not supported by this browser')
  const knownPorts = typeof nav.serial.getPorts === 'function' ? await nav.serial.getPorts() : []
  const port = knownPorts[0] ?? await nav.serial.requestPort()
  await port.open({ baudRate: 9600 })
  const writer = port.writable?.getWriter()
  if (!writer) throw new Error('Serial printer is not writable')
  await writer.write(escposBytes(buildReceiptText(input)))
  writer.releaseLock()
  await port.close()
  return 'Serial printer'
}

export async function printReceipt(options: PrintReceiptOptions): Promise<string> {
  if (options.method === 'webusb') return printWithWebUsb(options)
  if (options.method === 'webserial') return printWithWebSerial(options)
  await printWithSystemDialog(options)
  return 'System print dialog'
}

export function downloadReceiptHtml(input: ReceiptPrintInput) {
  const blob = new Blob([buildReceiptHtml(input)], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `receipt-${input.order.documentId}.html`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
